import React, { useEffect, useMemo, useState } from 'react'
import { useHistory } from 'react-router-dom'
import { useLocalStorageState } from 'ahooks'
import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'
import {
  Table,
  Row,
  Col,
  Button,
  DatePicker,
  Flex,
  Popover,
  Select,
  Tooltip,
  Typography,
} from 'antd'
import {
  ReloadOutlined,
  FilterOutlined,
  DownloadOutlined,
  ExceptionOutlined,
  EditOutlined,
  CheckOutlined,
} from '@ant-design/icons'
import { useDispatch } from 'react-redux'
import { useIntl } from 'react-intl'
import Papa from 'papaparse'
import { saveAs } from 'file-saver'
import { setModal } from '../actions/modal-actions'
import { MODAL_SELECT_TESTZONE } from '../constants'
import { useUserPlant, useUserTestZones } from '../hooks/User'
import { useGetUserTestZones } from '../hooks/TestZones'
import { useFaults } from '../hooks/Faults'
import { useMakeModelsByPlant } from '../hooks/MakeModels'
import { openTabWithVehicleTestByResultId } from '../api/vehicle-tests-api'
import { PlantButton } from '../Reports/PlantButton'
import { navigateToIncidentMgmtPage } from './IncidentMgmt'
import { ROOT_CAUSE_KEYS } from './PossibleRootCauses'
import { truncate, capitalizeAllWords } from '../helpers/utils'

dayjs.extend(relativeTime)

const { Text } = Typography

const TestZoneButton = () => {
  const intl = useIntl()
  const [userTestZones] = useUserTestZones()
  const dispatch = useDispatch()
  const label = intl.formatMessage({
    id: 'incidentMgmt.incidentList.testZonesLabel',
    defaultMessage: 'Test Zones',
  })
  return (
    <div className="test-zone-button">
      {label}:&nbsp;
      <Tooltip title={userTestZones.map((tz) => tz.description).join(', ')}>
        <Text
          underline
          style={{ cursor: 'pointer' }}
          onClick={() => dispatch(setModal(MODAL_SELECT_TESTZONE))}
        >
          {userTestZones.length > 0
            ? `${userTestZones.length} selected`
            : intl.formatMessage({
                id: 'incidentMgmt.incidentList.selectTestZones',
                defaultMessage: 'Select test zones',
              })}
        </Text>
      </Tooltip>
    </div>
  )
}

const IssueIdButton = ({ fault, onClick }) => {
  const intl = useIntl()
  return (
    <Tooltip title={truncate(fault.attachedIssue?.description)}>
      <Button onClick={onClick}>
        <ExceptionOutlined />{' '}
        {fault.attachedIssue
          ? fault.attachedIssue.id
          : intl.formatMessage({
              id: 'incidentMgmt.incidentList.associate',
              defaultMessage: 'Associate',
            })}
      </Button>
    </Tooltip>
  )
}

const RepairNoteCell = ({ fault }) => {
  const intl = useIntl()
  const history = useHistory()
  // "Reviewed" (green) covers three ways a fault can be considered handled:
  // its repair note has been attached to an issue -- attaching it is the
  // review, so this doesn't depend on that issue's open/closed status -- a
  // root cause was identified directly (problem_log.type isn't the generic
  // 'other'), or an issue is already attached to the fault itself, regardless
  // of whether a repair note was ever filed.
  const isReviewed =
    !!fault.repair_note_issue_status ||
    fault.type !== 'other' ||
    !!fault.attachedIssue
  if (!fault.repair_note_problem_log_id && !isReviewed) {
    return <span>—</span>
  }
  const openRepairNote = () =>
    history.push(`/production-line-dashboard/repair-note/fault/${fault.id}`, {
      fault,
      existingProblemLogId: fault.repair_note_problem_log_id,
      existingAnnotation: fault.repair_note_annotation,
      existingDtcs: fault.repair_note_dtcs ?? [],
    })
  if (isReviewed) {
    return (
      <Tooltip title={fault.repair_note_annotation}>
        <span
          className="repair-note-done"
          style={{ cursor: 'pointer' }}
          onClick={openRepairNote}
        >
          <EditOutlined style={{ color: 'var(--color-component-success)' }} />{' '}
          <CheckOutlined style={{ color: 'var(--color-component-success)' }} />
        </span>
      </Tooltip>
    )
  }
  return (
    <Tooltip title={fault.repair_note_annotation}>
      <span
        className="repair-note-pending"
        style={{ color: 'var(--color-component-danger)', cursor: 'pointer' }}
        onClick={openRepairNote}
      >
        <EditOutlined />{' '}
        {intl.formatMessage({
          id: 'incidentMgmt.incidentList.pending',
          defaultMessage: 'Pending',
        })}
      </span>
    </Tooltip>
  )
}

// Derives the Issue Status column text from the attached issue's status/clean_point,
// since "Contained" has no dedicated backend value (open + clean_point set = Contained).
const IssueStatusCell = ({ fault }) => {
  const intl = useIntl()
  const issue = fault.attachedIssue
  if (!issue) {
    return <span>-</span>
  }
  if (issue.status === 'closed') {
    return (
      <span>
        {intl.formatMessage({
          id: 'incidentMgmt.incidentList.statusClosed',
          defaultMessage: 'CLOSED',
        })}
      </span>
    )
  }
  if (issue.clean_point) {
    return (
      <span>
        {intl.formatMessage({
          id: 'incidentMgmt.incidentList.statusContained',
          defaultMessage: 'CONTAINED',
        })}
      </span>
    )
  }
  return (
    <span>
      {intl.formatMessage({
        id: 'incidentMgmt.incidentList.statusOpen',
        defaultMessage: 'OPEN',
      })}
    </span>
  )
}

// Formats issue.possible_root_causes (populated by the same "Possible Root
// Causes"/"4M" tab in both the full and compressed issue creation
// interfaces) as "Man - forgot to torque bolt, Design" -- categories with no
// free text entered show just the category name, categories never toggled
// on (value === null) are omitted entirely.
const formatRootCause = (possibleRootCauses, intl) => {
  const entries = ROOT_CAUSE_KEYS.map((key) => {
    const value = possibleRootCauses?.[key]
    if (value === null || value === undefined) {
      return null
    }
    const label = capitalizeAllWords(
      intl.formatMessage({
        id: `incidentMgmt.possibleRootCauses.${key}`,
        defaultMessage: key,
      }),
    )
    return value ? `${label} - ${value}` : label
  }).filter((entry) => !!entry)

  if (entries.length === 0) {
    return intl.formatMessage({
      id: 'incidentMgmt.incidentList.fourMAnalysisNone',
      defaultMessage: '--',
    })
  }
  return entries.join(', ')
}

// Renders "Label: value" as a single unconstrained-width flex item so it
// only wraps internally if its own content is too long for the row -- never
// just because a narrow fixed-width column forced it to.
const DetailField = ({ label, children }) => (
  <span className="expanded-detail-field">
    <strong>{label}:</strong> {children}
  </span>
)

const ExpandedFaultDetails = ({ fault }) => {
  const intl = useIntl()
  const issue = fault.attachedIssue
  return (
    <div className="expanded-incident-details">
      <Flex wrap gap="large" className="expanded-fault-details">
        <DetailField
          label={intl.formatMessage({
            id: 'incidentMgmt.incidentList.problemLogCode',
            defaultMessage: 'Problem Log Code',
          })}
        >
          {fault.problem_code || fault.type}
        </DetailField>
        <DetailField
          label={intl.formatMessage({
            id: 'incidentMgmt.incidentList.annotation',
            defaultMessage: 'Annotation',
          })}
        >
          {fault.note}
        </DetailField>
        <DetailField
          label={intl.formatMessage({
            id: 'incidentMgmt.incidentList.vehicleStatus',
            defaultMessage: 'Vehicle Status',
          })}
        >
          {fault.vin_status || '-'}
        </DetailField>
        <DetailField
          label={intl.formatMessage({
            id: 'incidentMgmt.incidentList.userId',
            defaultMessage: 'User ID',
          })}
        >
          {fault.created_by}
        </DetailField>
        <DetailField
          label={`${fault.DTCs.length} ${intl.formatMessage({
            id: 'incidentMgmt.incidentList.dtcs',
            defaultMessage: 'DTCs',
          })}`}
        >
          {fault.DTCs.map((dtc) => `${dtc.acronym} - ${dtc.dtc}`).join(', ')}
        </DetailField>
      </Flex>
      {issue && (
        <Flex wrap gap="large" className="expanded-issue-details">
          <DetailField
            label={intl.formatMessage({
              id: 'incidentMgmt.incidentList.issueDescription',
              defaultMessage: 'Issue Description',
            })}
          >
            {issue.description}
          </DetailField>
          <DetailField
            label={intl.formatMessage({
              id: 'incidentMgmt.incidentList.issueCreator',
              defaultMessage: 'Issue Creator',
            })}
          >
            {issue.created_by}
          </DetailField>
        </Flex>
      )}
      {issue && (
        <div className="expanded-issue-four-m-analysis">
          <DetailField
            label={intl.formatMessage({
              id: 'incidentMgmt.incidentList.fourMAnalysis',
              defaultMessage: '4M Analysis',
            })}
          >
            {formatRootCause(issue.possible_root_causes, intl)}
          </DetailField>
        </div>
      )}
    </div>
  )
}

const exportIncidents = (intl, faults) => {
  const headers = {
    vin: intl.formatMessage({
      id: 'incidentMgmt.incidentList.csvExportHeaders.vin',
      defaultMessage: 'VIN',
    }),
    sequence: intl.formatMessage({
      id: 'incidentMgmt.incidentList.csvExportHeaders.sequence',
      defaultMessage: 'Sequence',
    }),
    model: intl.formatMessage({
      id: 'incidentMgmt.incidentList.csvExportHeaders.model',
      defaultMessage: 'Make Model',
    }),
    timestamp: intl.formatMessage({
      id: 'incidentMgmt.incidentList.csvExportHeaders.timestamp',
      defaultMessage: 'Time Stamp',
    }),
    testZone: intl.formatMessage({
      id: 'incidentMgmt.incidentList.csvExportHeaders.testZone',
      defaultMessage: 'Test Zone',
    }),
    issueId: intl.formatMessage({
      id: 'incidentMgmt.incidentList.csvExportHeaders.issueId',
      defaultMessage: 'Issue ID',
    }),
    repairNote: intl.formatMessage({
      id: 'incidentMgmt.incidentList.csvExportHeaders.repairNote',
      defaultMessage: 'Repair Note',
    }),
    status: intl.formatMessage({
      id: 'incidentMgmt.incidentList.csvExportHeaders.status',
      defaultMessage: 'Status',
    }),
  }
  const data = faults.map((fault) => ({
    [headers.vin]: fault.vin,
    [headers.sequence]: fault.sequence_number,
    [headers.model]: fault.model_description,
    [headers.timestamp]: fault.test_timestamp,
    [headers.testZone]: fault.hardware_location_id,
    [headers.issueId]: fault.attachedIssue ? fault.attachedIssue.id : '',
    [headers.repairNote]: fault.repair_note_problem_log_id
      ? fault.repair_note_annotation ?? ''
      : '',
    [headers.status]: fault.attachedIssue
      ? fault.attachedIssue.status === 'closed'
        ? 'CLOSED'
        : fault.attachedIssue.clean_point
          ? 'CONTAINED'
          : 'OPEN'
      : '',
  }))
  const dataString = Papa.unparse(data)
  const blob = new Blob(['﻿' + dataString], {
    type: 'text/csv;charset=utf-8',
  })
  saveAs(blob, 'incident-list.csv')
}

export const IncidentList = () => {
  const intl = useIntl()
  const history = useHistory()
  const [plant] = useUserPlant()
  const [userTestZones] = useUserTestZones()
  const { isLoading: testZonesLoading, allTestZones } = useGetUserTestZones()
  const [testZonesBySpecifier, setTestZonesBySpecifier] = useState({})
  useEffect(() => {
    if (testZonesLoading || !plant) {
      return
    }
    const tzBySpecifier = {}
    allTestZones.forEach((tz) => {
      if (tz.plant.name.toUpperCase() === plant.toUpperCase()) {
        tzBySpecifier[tz.specifier] = tz
      }
    })
    setTestZonesBySpecifier(tzBySpecifier)
  }, [allTestZones, testZonesLoading, plant])

  // Persisted the same way the Fault Report filters are (see Reports.js's
  // 'reports-query' useLocalStorageState) -- one storage-backed object for
  // all filter fields, merged on each change. startDate is stored as an ISO
  // string rather than a dayjs instance since the default JSON (de)serializer
  // can't round-trip a dayjs object back into one.
  const [persistedFilters, setPersistedFilters] = useLocalStorageState(
    'incident-list-filters',
    {
      defaultValue: {
        startDate: dayjs().subtract(1, 'day').toISOString(),
        selectedMakeModelIds: [],
        issueStatusFilter: 'all',
      },
      listenStorageChange: true,
    },
  )
  const startDate = persistedFilters.startDate
    ? dayjs(persistedFilters.startDate)
    : null
  const { selectedMakeModelIds, issueStatusFilter } = persistedFilters

  const [endDate, setEndDate] = useState(() => dayjs())
  const [lastFetchedAt, setLastFetchedAt] = useState(null)

  const { makeModels } = useMakeModelsByPlant({ plantName: plant })

  const onStartDateChange = (value) => {
    setPersistedFilters({
      ...persistedFilters,
      startDate: value ? value.toISOString() : null,
    })
    setEndDate(dayjs())
  }

  const onSelectedMakeModelIdsChange = (value) => {
    setPersistedFilters({ ...persistedFilters, selectedMakeModelIds: value })
  }

  const onIssueStatusFilterChange = (value) => {
    setPersistedFilters({ ...persistedFilters, issueStatusFilter: value })
  }

  const { faults, isLoading, isFetching } = useFaults({
    makeModelIds: selectedMakeModelIds,
    stationIds: [],
    start_at: startDate ? startDate.toISOString() : null,
    end_at: endDate ? endDate.toISOString() : null,
    plants: plant,
    testZones: userTestZones.map((tz) => tz.specifier),
    includeRepairNotes: true,
  })

  // Issue-status filtering happens client-side since fault.attachedIssue is
  // already embedded in every fetched fault (same data IssueStatusCell reads
  // to render the column) -- no backend support needed. Model filtering
  // happens server-side via makeModelIds above instead, since that's already
  // wired end-to-end and cuts down what's fetched in the first place.
  const filteredFaults = useMemo(() => {
    if (!faults || issueStatusFilter === 'all') {
      return faults
    }
    return faults.filter((fault) => {
      const issue = fault.attachedIssue
      if (issueStatusFilter === 'no_issue') {
        return !issue
      }
      if (issueStatusFilter === 'open') {
        return !!issue && issue.status !== 'closed'
      }
      if (issueStatusFilter === 'closed') {
        return !!issue && issue.status === 'closed'
      }
      return true
    })
  }, [faults, issueStatusFilter])

  // Bumping endDate to "now" changes the useFaults query key, which triggers
  // a refetch -- this is the only refresh mechanism; there is no auto-polling.
  const onRefresh = () => {
    setEndDate(dayjs())
  }

  useEffect(() => {
    if (faults) {
      setLastFetchedAt(Date.now())
    }
  }, [faults])

  const columns = [
    {
      title: intl.formatMessage({
        id: 'incidentMgmt.incidentList.vin',
        defaultMessage: 'VIN',
      }),
      dataIndex: 'vin',
      render: (vin, fault) => (
        <a
          onClick={() =>
            openTabWithVehicleTestByResultId({
              vehicleTestResultId: fault.vehicle_test_result_id,
              componentId:
                fault.component_id || fault.harness || fault.device_name,
            })
          }
        >
          {vin}
        </a>
      ),
    },
    {
      title: intl.formatMessage({
        id: 'incidentMgmt.incidentList.sequence',
        defaultMessage: 'Sequence',
      }),
      dataIndex: 'sequence_number',
    },
    {
      title: intl.formatMessage({
        id: 'incidentMgmt.incidentList.model',
        defaultMessage: 'Make Model',
      }),
      dataIndex: 'model_description',
    },
    {
      title: intl.formatMessage({
        id: 'incidentMgmt.incidentList.timestamp',
        defaultMessage: 'Time Stamp',
      }),
      dataIndex: 'test_timestamp',
      render: (testTimestamp) =>
        testTimestamp
          ? dayjs(testTimestamp).utc().local().format('MM/DD/YYYY h:mma')
          : '',
    },
    {
      title: intl.formatMessage({
        id: 'incidentMgmt.incidentList.testZone',
        defaultMessage: 'Test Zone',
      }),
      dataIndex: 'hardware_location_id',
      render: (hardwareLocationId) => {
        if (testZonesBySpecifier.hasOwnProperty(hardwareLocationId)) {
          const tz = testZonesBySpecifier[hardwareLocationId]
          return tz.description ? tz.description : tz.specifier
        }
        return hardwareLocationId
      },
    },
    {
      title: intl.formatMessage({
        id: 'incidentMgmt.incidentList.issueId',
        defaultMessage: 'Issue ID',
      }),
      render: (fault) => (
        <IssueIdButton
          fault={fault}
          onClick={() =>
            fault.attachedIssue
              ? navigateToIncidentMgmtPage(fault)
              : history.push(`/incidents/associate/${fault.id}`, { fault })
          }
        />
      ),
    },
    {
      title: intl.formatMessage({
        id: 'incidentMgmt.incidentList.repairNote',
        defaultMessage: 'Repair Note',
      }),
      render: (fault) => <RepairNoteCell fault={fault} />,
    },
    {
      title: intl.formatMessage({
        id: 'incidentMgmt.incidentList.status',
        defaultMessage: 'Status',
      }),
      render: (fault) => <IssueStatusCell fault={fault} />,
    },
  ]

  const makeModelOptions = (makeModels ?? []).map((makeModel) => ({
    value: makeModel.id,
    label: `${makeModel.make} - ${makeModel.model} - ${makeModel.year} - ${makeModel.phase}`,
  }))

  const issueStatusOptions = [
    {
      value: 'all',
      label: intl.formatMessage({
        id: 'incidentMgmt.incidentList.filterStatusAll',
        defaultMessage: 'All',
      }),
    },
    {
      value: 'no_issue',
      label: intl.formatMessage({
        id: 'incidentMgmt.incidentList.filterStatusNoIssue',
        defaultMessage: 'No Issue',
      }),
    },
    {
      value: 'open',
      label: intl.formatMessage({
        id: 'incidentMgmt.incidentList.statusOpen',
        defaultMessage: 'OPEN',
      }),
    },
    {
      value: 'closed',
      label: intl.formatMessage({
        id: 'incidentMgmt.incidentList.statusClosed',
        defaultMessage: 'CLOSED',
      }),
    },
  ]

  const filtersContent = (
    <Flex vertical gap={12} style={{ minWidth: 240 }}>
      <DatePicker
        value={startDate}
        onChange={onStartDateChange}
        placeholder={intl.formatMessage({
          id: 'incidentMgmt.incidentList.startDatePlaceholder',
          defaultMessage: 'Start Date',
        })}
        // Default calendar z-index (antd's internal zIndexPopupBase + a fixed
        // offset, ~1150) sits well below this Popover's (zIndexPopup: 8000 in
        // ComponentThemeGenerator.js, ~z-index 8000) -- antd's automatic
        // parent/child z-index nesting only applies to "container" types
        // (Modal/Drawer/Popover/...), not DatePicker, so it doesn't inherit
        // the Popover's stacking. Pin it just above instead.
        styles={{ popup: { root: { zIndex: 8001 } } }}
      />
      <div>
        <div>
          {intl.formatMessage({
            id: 'incidentMgmt.incidentList.filterModel',
            defaultMessage: 'Model',
          })}
        </div>
        <Select
          mode="multiple"
          allowClear
          style={{ width: '100%' }}
          value={selectedMakeModelIds}
          onChange={onSelectedMakeModelIdsChange}
          options={makeModelOptions}
          placeholder={intl.formatMessage({
            id: 'incidentMgmt.incidentList.filterModelPlaceholder',
            defaultMessage: 'All Models',
          })}
        />
      </div>
      <div>
        <div>
          {intl.formatMessage({
            id: 'incidentMgmt.incidentList.filterIssueStatus',
            defaultMessage: 'Issue Status',
          })}
        </div>
        <Select
          style={{ width: '100%' }}
          value={issueStatusFilter}
          onChange={onIssueStatusFilterChange}
          options={issueStatusOptions}
        />
      </div>
    </Flex>
  )

  return (
    <div id="incident-list-page">
      <Row
        id="incident-list-filter-bar"
        align="middle"
        justify="space-between"
        style={{ marginBottom: 16 }}
      >
        <Col>
          <Row gutter={24}>
            <Col>
              <PlantButton />
            </Col>
            <Col>
              <TestZoneButton />
            </Col>
          </Row>
        </Col>
        <Col>
          <Row gutter={8} align="middle">
            <Col>
              <Popover
                content={filtersContent}
                title={intl.formatMessage({
                  id: 'incidentMgmt.incidentList.filters',
                  defaultMessage: 'Filters',
                })}
                trigger="click"
              >
                <Button icon={<FilterOutlined />}>
                  {intl.formatMessage({
                    id: 'incidentMgmt.incidentList.filters',
                    defaultMessage: 'Filters',
                  })}
                </Button>
              </Popover>
            </Col>
            <Col>
              <Button
                icon={<DownloadOutlined />}
                disabled={!filteredFaults || filteredFaults.length === 0}
                onClick={() => exportIncidents(intl, filteredFaults)}
              >
                {intl.formatMessage({
                  id: 'incidentMgmt.incidentList.export',
                  defaultMessage: 'Export',
                })}
              </Button>
            </Col>
            <Col>
              <Button icon={<ReloadOutlined />} onClick={onRefresh} />
              {lastFetchedAt && (
                <span className="last-fetched-at">
                  {' '}
                  {dayjs(lastFetchedAt).fromNow()}
                </span>
              )}
            </Col>
          </Row>
        </Col>
      </Row>
      {!startDate ? (
        <p className="incident-list-help-message">
          {intl.formatMessage({
            id: 'incidentMgmt.incidentList.selectStartDateHelp',
            defaultMessage: 'Select a Start Date to see incidents.',
          })}
        </p>
      ) : (
        <Table
          id="incident-list-table"
          columns={columns}
          dataSource={filteredFaults || []}
          rowKey="id"
          loading={isLoading || isFetching}
          pagination={{
            pageSize: 10,
            hideOnSinglePage: true,
            showSizeChanger: false,
          }}
          expandable={{
            expandedRowRender: (fault) => (
              <ExpandedFaultDetails fault={fault} />
            ),
          }}
        />
      )}
    </div>
  )
}
